import { Fragment, type ReactElement } from 'react'
import type { VarlessCopyId } from '@npc/copy'

import { segmentsUnchecked, type CopyId, type VarsFor } from './index'

/**
 * 一句模板文案。
 *
 * `<T id="host.summary.heading" />` 比 `{render('host.summary.heading')}` 短，
 * 也让「屏里出现的是 id」这件事在 JSX 里一眼看得见。
 *
 * `vars` 该不该必填由 id 决定：不带变量的模板不许传，带变量的必须传，
 * 变量名对不上也是编译期错误——与 `render` 一样，这层保证不能因为换成组件就丢。
 *
 * 认 `**加粗**`，因为 `docs/copy` 的模板里本来就有这个记号。
 * 换行按原文换（设计稿里登录页那句标题是硬换行），所以外层给了 `whitespace-pre-line`。
 *
 * 合并后文案目录变大，原来的 `K extends VarlessCopyId` 分配条件会触发 TS2859。
 * 改成与 `render` 相同的重载；第三条接住 `id: CopyId` 的动态调用。
 */
type VarfulCopyId = Exclude<CopyId, VarlessCopyId>

type Common = {
  /** 加粗那几段用什么字重。设计稿里有 500 也有 600 两种。 */
  strongClassName?: string
}

export type TProps<K extends CopyId> = { id: K } & Common & {
  vars?: VarsFor<K>
}

export function T<K extends VarlessCopyId>(props: { id: K } & Common): ReactElement
export function T<K extends VarfulCopyId>(props: { id: K; vars: VarsFor<K> } & Common): ReactElement
export function T<K extends CopyId>(props: { id: K; vars?: VarsFor<K> } & Common): ReactElement
export function T({ id, vars, strongClassName = 'font-medium' }: { id: CopyId; vars?: Record<string, string | number> } & Common): ReactElement {
  const parts = segmentsUnchecked(id, vars)

  // 没有加粗记号时不套一层多余的 Fragment 数组，省得 React 抱怨 key
  if (parts.length === 1 && !parts[0]!.strong) return <>{parts[0]!.text}</>

  return (
    <>
      {parts.map((part, index) =>
        part.strong ? (
          <strong key={index} className={strongClassName}>
            {part.text}
          </strong>
        ) : (
          <Fragment key={index}>{part.text}</Fragment>
        ),
      )}
    </>
  )
}
